import router from '@/router'
import { isObject } from '@core/utils/utils'
import { computed } from '@vue/composition-api'
import useJwt from '@/auth/jwt/useJwt'
import store from '@/store'

/**
 * Return which component to render based on it's data/context
 * @param {Object} item nav menu item
 */
export const resolveVerticalNavMenuItemComponent = item => {
  if (item.header) return 'vertical-nav-menu-header'
  if (item.children && item.children.length > 0) return 'vertical-nav-menu-group'
  return 'vertical-nav-menu-link'
}

/**
 * Return which component to render based on it's data/context
 * @param {Object} item nav menu item
 */
export const resolveHorizontalNavMenuItemComponent = item => {
  if (item.children) return 'horizontal-nav-menu-group'
  return 'horizontal-nav-menu-link'
}

/**
 * Return route name for navigation link
 * If link is string then it will assume it is route-name
 * IF link is object it will resolve the object and will return the link
 * @param {Object, String} link navigation link object/string
 */
export const resolveNavDataRouteName = link => {
  if (isObject(link.route)) {
    const { route } = router.resolve(link.route)
    return route.name
  }
  return link.route
}

/**
 * Check if nav-link is active
 * @param {Object} link nav-link object
 */
export const isNavLinkActive = link => {
  // Matched routes array of current route
  const matchedRoutes = router.currentRoute.matched

  // Check if provided route matches route's matched route
  const resolveRoutedName = resolveNavDataRouteName(link)

  if (!resolveRoutedName) return false

  return matchedRoutes.some(route => route.name === resolveRoutedName || route.meta.navActiveLink === resolveRoutedName)
}

/**
 * Check if nav group is
 * @param {Array} children Group children
 */
export const isNavGroupActive = children => {
  // Matched routes array of current route
  const matchedRoutes = router.currentRoute.matched

  return children.some(child => {
    // If child have children => It's group => Go deeper(recursive)
    if (child.children) {
      return isNavGroupActive(child.children)
    }

    // else it's link => Check for matched Route
    return isNavLinkActive(child, matchedRoutes)
  })
}

/**
 * Return b-link props to use
 * @param {Object, String} item navigation routeName or route Object provided in navigation data
 */
// prettier-ignore
export const navLinkProps = item => computed(() => {
  const props = {}

  // If route is string => it assumes => Create route object from route name
  // If route is not string => It assumes it's route object => returns route object
  if (item.route) props.to = typeof item.route === 'string' ? { name: item.route } : item.route
  else if (item.id) props.to = { name: 'dashboard', params: { id: item.id } }
  else {
    props.href = item.href
    props.target = '_blank'
    props.rel = 'nofollow'
  }

  if (!props.target) props.target = item.target || null

  return props
})

export const createMenu = async () => {
  if (!store.state.verticalMenu.menuLinks) {
    await useJwt.axiosIns.get('page/menu').then(menuData => {
      if (menuData.data && menuData.data.length) {
        const menuLinkItems = menuData.data.filter(x => !x.parentId)
        store.commit('verticalMenu/UPDATE_VERTICAL_MENU_LINKS', menuLinkItems)
      }
    })
  }
}

export const refreshMenu = async () => {
  await useJwt.axiosIns.get('page/menu').then(menuData => {
    if (menuData.data && menuData.data.length) {
      const menuLinkItems = menuData.data.filter(x => !x.parentId)
      store.commit('verticalMenu/UPDATE_VERTICAL_MENU_LINKS', menuLinkItems)
    }
  })
}

export const periodList = async () => {
  if (!store.state.globals.periodList) {
    await useJwt.axiosIns.get('project/allperiods').then(data => {
      const periodItems = data.data.sort((a, b) => b - a)
      const latestPeriod = periodItems[0]
      const firstPeriod = periodItems[0]

      // initial LAST 3 periods
      // if (periodItems.length > 2) {
      //   // eslint-disable-next-line prefer-destructuring
      //   firstPeriod = periodItems[2]
      // } else if (periodItems.length > 1) {
      //   // eslint-disable-next-line prefer-destructuring
      //   firstPeriod = periodItems[1]
      // }

      store.commit('globals/UPDATE_PERIODS', periodItems)
      store.commit('globals/UPDATE_GLOBAL_FILTER', {
        fromWave: firstPeriod, toWave: latestPeriod, rolling: 1, filterConfig: { filters: [] },
      })
    })
  }
}

export const hotAlertCount = async () => {
  await useJwt.axiosIns.get('hotalert/count').then(data => {
    store.commit('globals/UPDATE_HOTALERT_COUNT', data.data)
  })
}

export const approvalCount = async () => {
  await useJwt.axiosIns.get('hotalert/approvalcount').then(data => {
    store.commit('globals/UPDATE_APPROVAL_COUNT', data.data)
  })
}

export const appealCount = async () => {
  await useJwt.axiosIns.get('hotalert/appealcount').then(data => {
    store.commit('globals/UPDATE_APPEAL_COUNT', data.data)
  })
}

export const checkFilterSyntax = (projectId, filters) => useJwt.axiosIns.post(`helper/checkFilterSyntax/${projectId}`, filters)
